/**
 * The board. The page this whole repository exists to serve.
 *
 * Reading order is the design: the headline verdict, then anything happening
 * right now, then the per-service detail, then history. Somebody who reads only
 * the first two inches should already have their answer.
 */

import { useState } from 'react';
import { ds } from '../ds';
import { useApp } from '../state';
import { Eyebrow, Footnote, Page, Panel, StatePill } from '../components/Chrome';
import { UptimeBars } from '../components/UptimeBars';
import { LatencyChart } from '../components/LatencyChart';
import { IncidentCard } from '../components/IncidentCard';
import { SubscribePanel } from '../components/SubscribePanel';
import { Link } from '../router';
import { formatRelative, humanDuration, OVERALL_TONE, STATE_TONE } from '../status';
import { formatUptime } from '@shared/status';
import { display, figure, monoPlain, RADIUS, tableHead } from '../theme';
import type { PublicMonitorWire } from '../api';

import { useI18n, useT } from '../i18n';
export function Status() {
  const t = useT();
  const { tn } = useI18n();
  const { status, summary, error, errorCode } = useApp();

  if (status === 'loading') {
    return (
      <Page>
        <Panel>
          <div style={{ ...monoPlain, textAlign: 'center', padding: '30px 0' }}>
            {t('status.loading')}
          </div>
        </Panel>
      </Page>
    );
  }

  if (!summary) {
    // A fresh deploy that has not been migrated is by far the likeliest reason
    // to be here, and it is a setup step rather than a fault — so it gets the
    // command rather than an apology. See `migrationsPending` in
    // worker/lib/http.ts for why the server can tell the difference.
    if (errorCode === 'migrations_pending') {
      return (
        <Page>
          <Panel style={{ display: 'grid', gap: 10 }}>
            <Eyebrow>{t('status.setup.eyebrow')}</Eyebrow>
            <h1 style={display(24)}>{t('status.setup.title')}</h1>
            <pre
              style={{
                ...monoPlain,
                background: 'var(--surface-sunken)',
                borderRadius: RADIUS.control,
                padding: '12px 14px',
                margin: 0,
                overflowX: 'auto'
              }}
            >
              npx wrangler d1 migrations apply mauvely-status --remote
            </pre>
          </Panel>
        </Page>
      );
    }

    return (
      <Page>
        <Panel>
          <h1 style={display(24)}>{t('status.error.title')}</h1>
          <p style={{ color: 'var(--text-muted)', marginBottom: 0 }}>
            {error ?? t('status.error.body')}
          </p>
        </Panel>
      </Page>
    );
  }

  const tone = OVERALL_TONE[summary.overall];

  return (
    <Page>
      {/* ── the headline ─────────────────────────────────────────────────── */}
      <Panel
        style={{
          background: tone.soft,
          display: 'grid',
          gap: 6,
          borderLeft: `4px solid ${tone.fill}`
        }}
      >
        <Eyebrow>{summary.page.title}</Eyebrow>
        <h1
          style={{
            ...display(30),
            color: tone.ink,
            display: 'flex',
            alignItems: 'center',
            gap: 10
          }}
        >
          <BannerIcon icon={tone.icon} />
          {tone.label}
        </h1>
        <Footnote>
          {summary.page.tagline} · {t('status.updated', { when: formatRelative(summary.updatedAt) })}
        </Footnote>
      </Panel>

      {/* ── anything happening now ───────────────────────────────────────── */}
      {summary.active.length > 0 && (
        <Panel style={{ display: 'grid', gap: 20 }}>
          {/* `tn` rather than a ternary: a language with more than two
              plural categories needs more than two branches, and Intl.PluralRules
              is what knows which. */}
          <Eyebrow>{tn('status.active', summary.active.length, { count: summary.active.length })}</Eyebrow>
          {summary.active.map((incident) => (
            <IncidentCard key={incident.id} incident={incident} />
          ))}
        </Panel>
      )}

      {/* ── the services ─────────────────────────────────────────────────── */}
      <Panel style={{ display: 'grid', gap: 4, padding: '20px 24px 24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 12,
            marginBottom: 8
          }}
        >
          <Eyebrow>{t('status.services')}</Eyebrow>
          <span style={tableHead} className="st-hide-narrow">
            {t('status.uptime_head')}
          </span>
        </div>

        {summary.monitors.length === 0 && (
          <Footnote>{t('status.monitors.empty')}</Footnote>
        )}

        {summary.monitors.map((monitor, i) => (
          <MonitorRow
            key={monitor.id}
            monitor={monitor}
            historyDays={summary.historyDays}
            // The legend is shown once, under the first row. Repeating it under
            // ninety bars for every service would be four times the ink for the
            // same information.
            showLegend={i === 0}
          />
        ))}
      </Panel>

      <SubscribePanel />

      {/* ── recent history ───────────────────────────────────────────────── */}
      <Panel style={{ display: 'grid', gap: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Eyebrow>{t('status.recent')}</Eyebrow>
          <Link to="/incidents" style={{ ...monoPlain, textDecoration: 'none' }}>
            {t('status.seeall')}
          </Link>
        </div>

        {summary.recent.length === 0 ? (
          // What UptimeRobot and every good status page says for an empty
          // period. "Nothing here" would read as a page that is not working.
          <Footnote>{t('status.recent.empty', { days: summary.historyDays })}</Footnote>
        ) : (
          summary.recent.map((incident) => <IncidentCard key={incident.id} incident={incident} />)
        )}
      </Panel>
    </Page>
  );
}

function BannerIcon({ icon }: { icon: string }) {
  const { Icon } = ds();
  return <Icon name={icon} size={28} />;
}

function MonitorRow({
  monitor,
  historyDays,
  showLegend
}: {
  monitor: PublicMonitorWire;
  historyDays: number;
  showLegend: boolean;
}) {
  const t = useT();
  const { Icon } = ds();
  const [open, setOpen] = useState(false);
  const tone = STATE_TONE[monitor.state];

  const downFor =
    monitor.state === 'down' && monitor.stateSince
      ? humanDuration(Date.now() / 1000 - monitor.stateSince)
      : null;

  return (
    <div
      style={{
        padding: '16px 0',
        borderTop: '1px solid var(--border-hairline)',
        display: 'grid',
        gap: 10
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          flexWrap: 'wrap'
        }}
      >
        <div style={{ display: 'grid', gap: 2, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-strong)' }}>
              {monitor.name}
            </span>
            <StatePill tone={tone} size="sm" />
            {downFor && <span style={monoPlain}>{t('status.downfor', { duration: downFor })}</span>}
          </div>
          {monitor.description && (
            <span style={{ ...monoPlain, fontSize: 11.5 }}>{monitor.description}</span>
          )}
          {monitor.url && (
            <a
              href={monitor.url}
              rel="noreferrer noopener"
              style={{ ...monoPlain, fontSize: 11.5, textDecoration: 'none' }}
            >
              {monitor.url} ↗
            </a>
          )}
          {/* A forced state is disclosed rather than passed off as measured. A
              status page that quietly overrides its own data is worth nothing;
              one that says "we have set this by hand, here is why" is worth
              more than the raw probe. */}
          {monitor.overridden && (
            <span
              style={{
                ...monoPlain,
                fontSize: 11.5,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5
              }}
            >
              <Icon name="info" size={12} />
              {monitor.overrideNote
                ? t('status.manual.note', { note: monitor.overrideNote })
                : t('status.manual')}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 14, alignItems: 'baseline' }}>
          <Uptime label={t('status.window.day')} value={monitor.uptime.day} />
          <Uptime label={t('status.window.week')} value={monitor.uptime.week} />
          <Uptime label={t('status.window.month')} value={monitor.uptime.month} />
          <Uptime label={t('status.window.quarter')} value={monitor.uptime.quarter} strong />
        </div>
      </div>

      <UptimeBars days={monitor.days} showLegend={showLegend} label={monitor.name} />

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          ...monoPlain,
          background: 'none',
          border: 'none',
          padding: '2px 0',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          justifySelf: 'start'
        }}
        aria-expanded={open}
      >
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={13} />
        {open ? 'Hide response times' : 'Response times'}
      </button>

      {open && (
        <div
          style={{
            background: 'var(--surface-sunken)',
            borderRadius: RADIUS.card,
            padding: '16px 18px'
          }}
        >
          <LatencyChart monitorId={monitor.id} monitorName={monitor.name} />
        </div>
      )}

      {/* The screen-reader and print equivalent of the strip. A bar chart of 90
          rects is unreadable to both, and the numbers are the part that
          matters — this is also the "table view" the contrast rule asks for
          when a fill sits below 3:1. */}
      <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        {`${monitor.name}: ${tone.label}. Uptime ${formatUptime(monitor.uptime.day)} over 24 hours, ` +
          `${formatUptime(monitor.uptime.week)} over 7 days, ${formatUptime(monitor.uptime.month)} over 30 days, ` +
          `${formatUptime(monitor.uptime.quarter)} over ${historyDays} days.`}
      </span>
    </div>
  );
}

function Uptime({
  label,
  value,
  strong
}: {
  label: string;
  value: number | null;
  strong?: boolean;
}) {
  return (
    <div style={{ display: 'grid', gap: 1, textAlign: 'right', minWidth: 46 }}>
      <span
        style={{
          ...figure,
          fontSize: strong ? 14 : 13,
          fontWeight: strong ? 700 : 500,
          color: strong ? 'var(--text-strong)' : 'var(--text-body)'
        }}
      >
        {formatUptime(value)}
      </span>
      <span style={{ ...tableHead, fontSize: 9 }}>{label}</span>
    </div>
  );
}
